/**
 * Centralized route paths for the application
 * Use these helpers for type-safe navigation
 */

export const ROUTES = {
	// ==========================================
	// REP CLUBS ROUTES
	// ==========================================
	REP_CLUBS: {
		// User/Member views (public or member-facing)
		LIST: '/rep-clubs',
		DETAIL: (clubId: string) => `/rep-clubs/${clubId}`,
		OFFERS: (clubId: string) => `/manage/rep-clubs/${clubId}/offers`,
		OFFER_DETAIL: (clubId: string, offerId: string) => `/manage/rep-clubs/${clubId}/offers/${offerId}`,
		BUY_POINTS: (clubId: string) => `/rep-clubs/${clubId}/buy-points`,
		EVENTS: (clubId?: string) => (clubId ? `/manage/rep-clubs/${clubId}/events` : `/manage/rep-clubs/events`),
		ACHIEVEMENT: (clubId: string, achievementId: string) => `/rep-clubs/${clubId}/achievement/${achievementId}`,

		// Owner/Management views (requires ownership)
		MANAGE: {
			ROOT: (clubId: string) => `/manage/rep-clubs/${clubId}`,
			EDIT: (clubId: string) => `/manage/rep-clubs/${clubId}/edit`,
			TEAM: (clubId: string) => `/manage/rep-clubs/${clubId}/team`,
			ACCOUNTS: (clubId: string) => `/manage/rep-clubs/${clubId}/accounts`,
			REP_POINTS: (clubId: string) => `/manage/rep-clubs/${clubId}/rep-points`,
			LOYALTY_TIERS: (clubId: string) => `/manage/rep-clubs/${clubId}/loyalty-tiers`,

			// Events management
			EVENTS: {
				LIST: (clubId?: string) => (clubId ? `/manage/rep-clubs/${clubId}/events` : `/manage/rep-clubs/events`),
				CREATE: (clubId?: string) =>
					clubId ? `/manage/rep-clubs/${clubId}/events/create` : `/manage/rep-clubs/events/create`,
				DETAIL: (clubId: string | undefined, eventId: string) =>
					clubId ? `/manage/rep-clubs/${clubId}/events/${eventId}` : `/manage/rep-clubs/events/${eventId}`,
				SERIES_DETAIL: (clubId: string | undefined, seriesId: string) =>
					clubId
						? `/manage/rep-clubs/${clubId}/events/series/${seriesId}`
						: `/manage/rep-clubs/events/series/${seriesId}`,
				EDIT: (clubId: string | undefined, eventId: string) =>
					clubId
						? `/manage/rep-clubs/${clubId}/events/${eventId}/edit`
						: `/manage/rep-clubs/events/${eventId}/edit`,
				REVENUE: (clubId?: string) =>
					clubId ? `/manage/rep-clubs/${clubId}/events/revenue` : `/manage/rep-clubs/events/revenue`,
				QUICK_LINK: (clubId?: string) =>
					clubId ? `/manage/rep-clubs/${clubId}/events/quick-link` : `/manage/rep-clubs/events/quick-link`,
				APPROVAL: (clubId: string | undefined, eventId: string) =>
					clubId
						? `/manage/rep-clubs/${clubId}/events/${eventId}/approval`
						: `/manage/rep-clubs/events/${eventId}/approval`,
				INVITE: (clubId: string | undefined, eventId: string) =>
					clubId
						? `/manage/rep-clubs/${clubId}/events/${eventId}/invite`
						: `/manage/rep-clubs/events/${eventId}/invite`,
				GUEST: (clubId: string | undefined, eventId: string) =>
					clubId
						? `/manage/rep-clubs/${clubId}/events/${eventId}/guest`
						: `/manage/rep-clubs/events/${eventId}/guest`,
				EVENT_REVENUE: (clubId: string | undefined, eventId: string) =>
					clubId
						? `/manage/rep-clubs/${clubId}/events/${eventId}/revenue`
						: `/manage/rep-clubs/events/${eventId}/revenue`,
			},

			// Offers management
			OFFERS: {
				LIST: (clubId: string) => `/manage/rep-clubs/${clubId}/offers`,
				CREATE: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create`,
				DETAIL: (clubId: string, offerId: string) => `/manage/rep-clubs/${clubId}/offers/${offerId}`,
				EDIT: (clubId: string, offerId: string) => `/manage/rep-clubs/${clubId}/offers/${offerId}/edit`,
				SELECT_TYPE: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/select-offer-type`,
				SELECT_SUB_TYPE: (clubId: string, offerType: string) =>
					`/manage/rep-clubs/${clubId}/offers/create/${offerType}`,

				// Way to reward members (create)
				WAY_TO_REWARD_MEMBERS: (clubId: string, subType: string) =>
					`/manage/rep-clubs/${clubId}/offers/create/way-to-reward-members/${subType}`,
				WAY_TO_REWARD_MEMBERS_WITH_ID: (clubId: string, subType: string, id: string) =>
					`/manage/rep-clubs/${clubId}/offers/create/way-to-reward-members/${subType}/${id}`,
				// Way to reward members (edit existing offer)
				WAY_TO_REWARD_MEMBERS_EDIT: (clubId: string, offerId: string, subType: string) =>
					`/manage/rep-clubs/${clubId}/offers/${offerId}/edit/way-to-reward-members/${subType}`,

				// Way to earn status
				WAY_TO_EARN_STATUS: {
					ACTION_AMOUNT: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-status/action-amount`,
					BUY_NOW: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-earn-status/buy-now`,
					INVITE_ONLY: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-status/invite-only`,
					LEVEL_UP: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-status/level-up`,
					PROMO: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-earn-status/promo`,
					STREAK: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-earn-status/streak`,
				},

				// Way to earn points
				WAY_TO_EARN_POINTS: {
					STREAK: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/streak`,
					SOCIAL_ACTION: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/social-action`,
					LOCATION: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/location`,
					SURVEY: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/survey`,
					BONUS: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/bonus`,
					DOWNLOAD: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/download`,
					INTERACT: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/interact`,
					BUY_PRODUCT: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-earn-points/buy-product`,
				},

				// Way to buy points
				WAY_TO_BUY_POINTS: {
					POINTS_BUNDLE: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-buy-points/points-bundle`,
					BONUS_BUYS: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-buy-points/bonus-buys`,
					TIME_SPAN: (clubId: string) =>
						`/manage/rep-clubs/${clubId}/offers/create/way-to-buy-points/time-span`,
					STREAK: (clubId: string) => `/manage/rep-clubs/${clubId}/offers/create/way-to-buy-points/streak`,
				},
			},

			// Achievements management
			ACHIEVEMENTS: {
				LIST: (clubId: string) => `/manage/rep-clubs/${clubId}/achievements`,
				CREATE: (clubId: string) => `/manage/rep-clubs/${clubId}/achievements/create`,
				DETAIL: (clubId: string, achievementId: string) =>
					`/manage/rep-clubs/${clubId}/achievements/${achievementId}`,
				EDIT: (clubId: string, achievementId: string) =>
					`/manage/rep-clubs/${clubId}/achievements/${achievementId}/edit`,
			},
		},

		// Rep Club creation
		CREATE: '/rep-clubs/create',
	},

	// ==========================================
	// REP POINTS ROUTES (User)
	// ==========================================
	REP_POINTS: {
		MY_POINTS: '/rep-points/my-points',
		TRADE_HISTORY: '/rep-points/trade-history',
		TRADE_REQUESTS: '/rep-points/trade-requests',
	},

	// ==========================================
	// OFFERS ROUTES (User)
	// ==========================================
	OFFERS: {
		LIST: (tab: string) => `/offers/${tab}`,
	},

	// ==========================================
	// EVENTS ROUTES
	// ==========================================
	EVENTS: {
		LIST: '/events',
		DETAIL: (eventId: string) => `/events/${eventId}`,
	},

	// ==========================================
	// PROFILE ROUTES
	// ==========================================
	PROFILE: {
		ROOT: '/profile',
		INFORMATION: '/profile/information',
		SOCIAL: '/profile/social',
		NOTIFICATIONS: '/profile/notifications',
		INVITES: '/profile/invites',
		SETTINGS: '/profile/settings',
	},

	// ==========================================
	// AUTH ROUTES
	// ==========================================
	AUTH: {
		LOGIN: '/auth/login',
		SIGN_UP: '/auth/sign-up',
		FORGOT_PASSWORD: '/auth/forgot-password',
		RESET_PASSWORD: '/auth/reset-password',
	},

	// ==========================================
	// OTHER ROUTES
	// ==========================================
	HOME: '/home',
	SEARCH: '/search',
	TERMS_OF_SERVICE: '/terms-of-service',
	PRIVACY_POLICY: '/privacy-policy',
} as const

// Type helper for route params
export type ClubId = string
export type OfferId = string
export type EventId = string
export type AchievementId = string
